/**
 * Onboarding Guards
 *
 * Two guards work together to route users through the post-signup
 * wizard. The flow has two layers:
 *
 *  1. **User onboarding** — once per human (firstName/lastName/phone,
 *     professional context, stated property count, marketing consent).
 *     Lives at `users/{uid}.profile.*`.
 *  2. **Workspace onboarding** — once per workspace, type-specific.
 *     Lives at `workspaces/{wid}.onboarding.*`.
 *
 * - `OnboardingCompletedGuard` protects the main app shell. It bounces
 *   the user to `/onboarding` until BOTH the personal block and the
 *   workspace block are completed (the latter only for owner-like types).
 *
 * - `OnboardingAccessGuard` protects `/onboarding/*`. It dispatches to
 *   the user-level steps first, then hands off to the workspace flow.
 *   `?relaunch=true` is the escape hatch from the settings "Relaunch
 *   tour" link, which flips the workspace flag back via
 *   `relaunchOnboarding`.
 *
 * Both follow the same pattern as `workspace-type.guard.ts`: wait for the
 * workspace signal, take(1), map to a `UrlTree` or `true`.
 */

import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
  UrlTree} from '@angular/router';
import {
  Router
} from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, catchError, combineLatest, filter, map, of, take, timeout } from 'rxjs';

import { WorkspaceStore } from '@shared/services/workspace.store';
import { WorkspaceType } from '@shared/definitions/workspace';
import {
  resolveAgencyOnboarding,
  resolveOnboardingAccess,
  resolveOnboardingCompleted,
} from './onboarding-routing';
import {
  clearOnboardingJustFinished,
  isOnboardingJustFinished,
} from '@app/onboarding-shell/services/onboarding.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { AuthService } from '@core/services/auth/auth.service';
import {
  USER_ONBOARDING_STEP_ROUTES,
  isUserOnboardingJustFinished,
} from '@app/onboarding-shell/services/user-onboarding.service';

/** True when the requested URL targets the user-level onboarding subset. */
function isUserOnboardingRoute(route: ActivatedRouteSnapshot): boolean {
  const segment = route.firstChild?.url[0]?.path;
  return !!segment && USER_ONBOARDING_STEP_ROUTES.includes(segment);
}

const WAIT_MS = 10000;

@Injectable({ providedIn: 'root' })
export class OnboardingCompletedGuard implements CanActivate {
  private readonly router = inject(Router);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly authStore = inject(AuthStore);
  private readonly authService = inject(AuthService);
  private readonly workspace$ = toObservable(this.workspaceStore.currentWorkspace);
  private readonly user$ = toObservable(this.authStore.user);
  private readonly userLoaded$ = toObservable(this.authStore.userLoaded);

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return combineLatest([this.workspace$, this.user$, this.userLoaded$]).pipe(
      // Wait for BOTH a workspace AND the user document to be hydrated from
      // Firestore. Without the `loaded` gate, the guard could read a partial
      // user (no `profile`) and wrongly send a returning user back through
      // onboarding on a fresh browser/device.
      filter(([ws, , loaded]) => ws !== null && loaded),
      take(1),
      timeout(WAIT_MS),
      map(([workspace, user]) => {
        // SECURITY (2026-07 empty-dashboard bug): an authenticated Firebase
        // session with NO `users/{uid}` document means registration never
        // completed (e.g. `createUserDocument` was rejected under a missing
        // App Check token or the signup gate). Such a session must NEVER reach
        // the app shell. Tear the orphan session down and return to login — a
        // bare redirect would loop, because AuthShell re-navigates any
        // authenticated user straight back to '/'.
        if (!user || !user.userUID) {
          void this.authService.logout();
          return false;
        }
        const type = (workspace?.type || 'personal') as WorkspaceType;
        const userProfileCompleted =
          !!user?.profile?.completedAt || isUserOnboardingJustFinished();
        const workspaceOnboardingCompleted = !!workspace?.onboarding?.completed;

        const decision = resolveOnboardingCompleted({
          type,
          userProfileCompleted,
          onUserStep: false,
          relaunch: false,
          workspaceOnboardingCompleted,
          onboardingJustFinished: isOnboardingJustFinished(),
        });

        // Side-effect parity with the original: once the workspace block is
        // genuinely complete, drop the session "just finished" sentinel.
        if (workspaceOnboardingCompleted) {
          clearOnboardingJustFinished();
        }

        return decision === true ? true : this.router.createUrlTree(decision);
      }),
      catchError(err => {
        console.error('[OnboardingCompletedGuard] error/timeout:', err);
        // SECURITY (2026-07 empty-dashboard bug): an authenticated Firebase
        // session with NO `users/{uid}` document must never reach the app
        // shell. Fail CLOSED: drop the orphan session and return to login.
        if (!this.authStore.hasUser()) {
          void this.authService.logout();
          return of(false);
        }

        // Registered user, but workspace/hydration stalled. The old
        // `of(true)` fail-open admitted incomplete signups to `/` and the
        // cockpit empty state ("Créez votre premier bien"). Prefer the
        // onboarding wizard; once hydration catches up, AccessGuard /
        // CompletedGuard will bounce finished users home.
        const user = this.authStore.user();
        const workspace = this.workspaceStore.currentWorkspace();
        if (user?.userUID && workspace) {
          const type = (workspace.type || 'personal') as WorkspaceType;
          const decision = resolveOnboardingCompleted({
            type,
            userProfileCompleted:
              !!user.profile?.completedAt || isUserOnboardingJustFinished(),
            onUserStep: false,
            relaunch: false,
            workspaceOnboardingCompleted: !!workspace.onboarding?.completed,
            onboardingJustFinished: isOnboardingJustFinished(),
          });
          return of(decision === true ? true : this.router.createUrlTree(decision));
        }

        const profileDone =
          !!user?.profile?.completedAt || isUserOnboardingJustFinished();
        return of(
          this.router.createUrlTree(
            profileDone ? ['/onboarding'] : ['/onboarding', 'user-profile'],
          ),
        );
      }),
    );
  }
}

@Injectable({ providedIn: 'root' })
export class OnboardingAccessGuard implements CanActivate {
  private readonly router = inject(Router);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly authStore = inject(AuthStore);
  private readonly workspace$ = toObservable(this.workspaceStore.currentWorkspace);
  private readonly user$ = toObservable(this.authStore.user);
  private readonly userLoaded$ = toObservable(this.authStore.userLoaded);

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return combineLatest([this.workspace$, this.user$, this.userLoaded$]).pipe(
      // User-level steps may run before a workspace exists (invitation signup
      // with skipPersonalWorkspace — #588). Workspace steps still require ws.
      filter(([ws, , loaded]) =>
        loaded && (ws !== null || isUserOnboardingRoute(route)),
      ),
      take(1),
      timeout(WAIT_MS),
      map(([workspace, user]) => {
        const type = (workspace?.type || 'personal') as WorkspaceType;
        const relaunch = route.queryParamMap.get('relaunch') === 'true';
        const userProfileCompleted =
          !!user?.profile?.completedAt || isUserOnboardingJustFinished();
        const workspaceOnboardingCompleted = !!workspace?.onboarding?.completed;

        const decision = resolveOnboardingAccess({
          type,
          userProfileCompleted,
          onUserStep: isUserOnboardingRoute(route),
          relaunch,
          workspaceOnboardingCompleted,
          onboardingJustFinished: isOnboardingJustFinished(),
        });

        // Side-effect parity with the original: once the workspace block is
        // complete and we're sending the user home, drop the sentinel (skipped
        // on relaunch, as before).
        if (workspaceOnboardingCompleted && !relaunch) {
          clearOnboardingJustFinished();
        }

        return decision === true ? true : this.router.createUrlTree(decision);
      }),
      catchError(err => {
        console.error('[OnboardingAccessGuard] error/timeout:', err);
        // Fail-closed (#592): never of(true) into a half-hydrated wizard.
        return of(this.router.createUrlTree(['/onboarding', 'user-profile']));
      }),
    );
  }
}

/**
 * Protects the top-level, full-screen `/agency/onboarding` route (declared
 * OUTSIDE the main `LayoutComponent`, so the agency wizard renders with no left
 * menu — the same lock pattern as the property-manager `/onboarding` shell).
 *
 * Sends the user through the shared user-profile step first (only the first
 * time they connect), bounces an already-configured agency to its dashboard,
 * and otherwise shows the wizard.
 */
@Injectable({ providedIn: 'root' })
export class AgencyOnboardingGuard implements CanActivate {
  private readonly router = inject(Router);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly authStore = inject(AuthStore);
  private readonly workspace$ = toObservable(this.workspaceStore.currentWorkspace);
  private readonly user$ = toObservable(this.authStore.user);
  private readonly userLoaded$ = toObservable(this.authStore.userLoaded);

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return combineLatest([this.workspace$, this.user$, this.userLoaded$]).pipe(
      // See OnboardingCompletedGuard: never decide on a not-yet-hydrated user.
      filter(([ws, , loaded]) => ws !== null && loaded),
      take(1),
      timeout(WAIT_MS),
      map(([workspace, user]) => {
        const type = (workspace?.type || 'personal') as WorkspaceType;
        const relaunch = route.queryParamMap.get('relaunch') === 'true';
        const userProfileCompleted =
          !!user?.profile?.completedAt || isUserOnboardingJustFinished();
        const workspaceOnboardingCompleted = !!workspace?.onboarding?.completed;

        const decision = resolveAgencyOnboarding({
          type,
          userProfileCompleted,
          onUserStep: false,
          relaunch,
          workspaceOnboardingCompleted,
          onboardingJustFinished: isOnboardingJustFinished(),
        });

        return decision === true ? true : this.router.createUrlTree(decision);
      }),
      catchError(err => {
        console.error('[AgencyOnboardingGuard] error/timeout:', err);
        // Fail-closed (#592): safe redirect into the universal user step.
        return of(this.router.createUrlTree(['/onboarding', 'user-profile']));
      }),
    );
  }
}
